/**
 * Hook to fetch task suggestions for a repository from the backend.
 * The server matches analysis gaps against available tasks and returns ready-made suggestions.
 */

import { useEffect, useState } from 'react';
import axios from 'axios';
import { useAuthenticatedApi } from './useAuthenticatedApi';
import { logger } from '../utils/logger';
import type { TaskSuggestion } from '../components/tasks/TaskSuggestionCard';

interface UseTaskSuggestionsReturn {
  suggestions: TaskSuggestion[];
  // Number of critical-severity gaps the scan found. Used to distinguish a
  // genuinely-clean empty state from one where critical issues exist but the
  // task catalog has no automated remediation for them (bug #323).
  criticalGapsCount: number;
  loading: boolean;
  error: string | null;
}

interface SuggestionsResponse {
  suggestions: TaskSuggestion[];
  criticalGapsCount?: number;
}

export function useTaskSuggestions(repositoryId: string | undefined): UseTaskSuggestionsReturn {
  const api = useAuthenticatedApi();
  const [suggestions, setSuggestions] = useState<TaskSuggestion[]>([]);
  const [criticalGapsCount, setCriticalGapsCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!repositoryId) {
      setLoading(false);
      return;
    }

    async function fetchSuggestions() {
      try {
        const response = await api.get<SuggestionsResponse>(`/tasks/suggestions/${repositoryId}`);
        const data = response.data?.suggestions || [];
        setSuggestions(Array.isArray(data) ? data : []);
        setCriticalGapsCount(
          typeof response.data?.criticalGapsCount === 'number'
            ? response.data.criticalGapsCount
            : 0,
        );
        setError(null);
      } catch (err: unknown) {
        logger.error('[useTaskSuggestions] Failed to fetch suggestions:', { error: err });
        const message = axios.isAxiosError(err) ? err.response?.data?.error : undefined;
        setError(message || 'Failed to load task suggestions');
        setSuggestions([]);
        setCriticalGapsCount(0);
      } finally {
        setLoading(false);
      }
    }

    fetchSuggestions();
  }, [api, repositoryId]);

  return { suggestions, criticalGapsCount, loading, error };
}
